<template>
  <div class="revision-plan">
    <div class="plan-document">
      <template v-if="parsed.ok">
        <header class="plan-header">
          <h1 class="plan-header__title">{{ parsed.title || 'Revision Plan' }}</h1>
          <div v-if="progress.totalCount.value > 0" class="plan-progress">
            <div class="plan-progress__label">
              <span class="plan-progress__micro">Overall progress</span>
              <span>
                {{ progress.completedCount.value }} of {{ progress.totalCount.value }} action items complete
                <template v-if="progress.dismissedCount.value > 0">
                  · {{ progress.dismissedCount.value }} dismissed
                </template>
              </span>
            </div>
            <div class="plan-progress__bar">
              <div class="plan-progress__fill" :style="{ width: progressPercent + '%' }"></div>
            </div>
          </div>
        </header>

        <div v-if="parsed.intro" class="plan-richtext plan-intro" v-html="render(parsed.intro)"></div>

        <template v-for="section in parsed.sections" :key="section.id">
          <div class="section-divider">
            <span class="section-divider__label">{{ section.title }}</span>
            <span class="section-divider__rule"></span>
          </div>

          <div
            v-if="section.bodyMarkdown"
            class="plan-richtext section-preamble"
            v-html="render(section.bodyMarkdown)"
          ></div>

          <!-- Priority articles -->
          <article v-for="priority in visiblePriorities(section)" :key="priority.id" :id="priority.id" class="priority">
            <div class="priority__header">
              <h3 class="priority__title">{{ priority.index }}. {{ priority.title }}</h3>
              <span v-if="pillFor(priority.level)" class="priority-pill" :class="`priority-pill--${priority.level}`">
                {{ pillFor(priority.level) }}
              </span>
            </div>
            <div v-if="priority.bodyMarkdown" class="plan-richtext" v-html="render(priority.bodyMarkdown)"></div>
            <ul v-if="priority.actions.length" class="action-list">
              <li
                v-for="(action, i) in priority.actions"
                :key="`${priority.id}-${i}`"
                class="action-item"
                :class="{ 'action-item--dismissed': progress.isDismissed(actionKey(priority, i)) }"
              >
                <label
                  class="action-item__label"
                  :class="{ 'action-item__label--done': progress.isChecked(actionKey(priority, i)) }"
                >
                  <input
                    type="checkbox"
                    class="action-item__checkbox"
                    :checked="progress.isChecked(actionKey(priority, i))"
                    :disabled="progress.isDismissed(actionKey(priority, i))"
                    @change="progress.toggle(actionKey(priority, i))"
                  />
                  <span class="plan-richtext action-item__text" v-html="renderAction(action)"></span>
                </label>
                <button
                  class="action-item__dismiss"
                  :title="progress.isDismissed(actionKey(priority, i)) ? 'Restore this item' : 'Dismiss this item'"
                  :aria-label="progress.isDismissed(actionKey(priority, i)) ? 'Restore this item' : 'Dismiss this item'"
                  @click="progress.toggleDismissed(actionKey(priority, i))"
                >
                  <QIcon :path="progress.isDismissed(actionKey(priority, i)) ? mdiRestore : mdiClose" :size="16" />
                </button>
              </li>
            </ul>
          </article>

          <button v-if="hiddenCount(section) > 0" class="show-more" @click="expandedSections.add(section.id)">
            Show {{ hiddenCount(section) }} more {{ hiddenCount(section) === 1 ? 'priority' : 'priorities' }} ⌄
          </button>

          <!-- Phase callout bands -->
          <div v-for="phase in section.phases" :key="phase.id" class="phase-callout">
            <div class="phase-callout__name">{{ phase.name }}</div>
            <div class="plan-richtext phase-callout__body" v-html="render(phase.bodyMarkdown)"></div>
            <div v-if="phase.priorityRefs.length" class="phase-callout__chips">
              <button
                v-for="ref in phase.priorityRefs"
                :key="`${phase.id}-ref-${ref}`"
                class="phase-chip"
                @click="jumpToPriority(ref)"
              >
                Priority {{ ref }}<template v-if="priorityTitle(ref)">: {{ priorityTitle(ref) }}</template>
              </button>
            </div>
          </div>
        </template>
      </template>

      <!-- Fallback: unparseable content renders exactly like a generic output -->
      <div v-else class="plan-richtext" v-html="render(content)"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiClose, mdiRestore } from '@mdi/js';
import { computed, reactive, toRef } from 'vue';
import { useRevisionProgress } from '../composables/useRevisionProgress';
import QIcon from './QIcon.vue';
import {
  parseRevisionPlan,
  type ActionItem,
  type PriorityLevel,
  type RevisionPriority,
  type RevisionSection,
} from '../lib/revisionPlanParser';
import { parseMarkdown, renderMarkdownChunk } from '../lib/renderOutput';

const props = defineProps<{ content: string; projectId: string; frameworkId: string }>();

const COLLAPSED_PRIORITY_COUNT = 2;

const parsed = computed(() => parseRevisionPlan(props.content));
const expandedSections = reactive(new Set<string>());

// Checkbox identity: hash of the action text, scoped by the priority's index so
// identical wording in two priorities doesn't share state.
function actionKey(priority: RevisionPriority, actionIndex: number): string {
  return `${priority.index}:${priority.actions[actionIndex]!.id}`;
}

const allActionKeys = computed(() =>
  parsed.value.sections.flatMap(s => s.priorities.flatMap(p => p.actions.map((_, i) => actionKey(p, i))))
);

const progress = useRevisionProgress(toRef(props, 'projectId'), toRef(props, 'frameworkId'), allActionKeys);

const progressPercent = computed(() =>
  progress.totalCount.value === 0 ? 0 : Math.round((progress.completedCount.value / progress.totalCount.value) * 100)
);

function render(markdown: string): string {
  return parseMarkdown(markdown);
}

function renderAction(action: ActionItem): string {
  // Render the full item (including nested sub-bullets) when present; the
  // outer list marker is hidden in CSS since the checkbox replaces it.
  if (action.markdown.includes('\n')) {
    return renderMarkdownChunk(action.markdown);
  }
  return renderMarkdownChunk(action.text)
    .replace(/^<p>/, '')
    .replace(/<\/p>\s*$/, '');
}

function pillFor(level: PriorityLevel): string | null {
  switch (level) {
    case 'critical':
      return 'Critical Priority';
    case 'secondary':
      return 'High Priority';
    case 'minor':
      return 'Refinement';
    default:
      return null;
  }
}

// Secondary/minor sections start collapsed past the first two priorities
function isCollapsible(section: RevisionSection): boolean {
  return (
    (section.level === 'secondary' || section.level === 'minor') &&
    section.priorities.length > COLLAPSED_PRIORITY_COUNT &&
    !expandedSections.has(section.id)
  );
}

function visiblePriorities(section: RevisionSection): RevisionPriority[] {
  return isCollapsible(section) ? section.priorities.slice(0, COLLAPSED_PRIORITY_COUNT) : section.priorities;
}

function hiddenCount(section: RevisionSection): number {
  return isCollapsible(section) ? section.priorities.length - COLLAPSED_PRIORITY_COUNT : 0;
}

function priorityByRef(ref: number): RevisionPriority | undefined {
  return parsed.value.sections.flatMap(s => s.priorities).find(p => p.index === ref);
}

function priorityTitle(ref: number): string {
  const title = priorityByRef(ref)?.title ?? '';
  return title.length > 32 ? title.slice(0, 32) + '…' : title;
}

function jumpToPriority(ref: number) {
  const priority = priorityByRef(ref);
  if (!priority) return;
  // Expand the section containing it first so the element exists
  const section = parsed.value.sections.find(s => s.priorities.includes(priority));
  if (section) expandedSections.add(section.id);
  requestAnimationFrame(() => {
    document.getElementById(priority.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}
</script>

<style scoped>
.revision-plan {
  max-width: 816px;
  margin: 0 auto;
}

.plan-document {
  background: white;
  border-radius: var(--border-radius, 8px);
  box-shadow: var(--shadow-lg, 0 10px 25px -5px rgba(0, 0, 0, 0.08));
  padding: 72px;
}

@media (max-width: 1040px) {
  .plan-document {
    padding: 48px;
    box-shadow: var(--shadow-md);
  }
}

@media (max-width: 768px) {
  .plan-document {
    padding: 24px;
  }
}

/* ─── Header & progress ─── */

.plan-header {
  margin-bottom: 2rem;
}

.plan-header__title {
  font-family: 'PT Serif', serif;
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 1.25rem;
  line-height: 1.2;
}

.plan-progress__label {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
}

.plan-progress__micro {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.plan-progress__bar {
  height: 4px;
  border-radius: 2px;
  background: var(--color-bg-tertiary, #f3f4f6);
  overflow: hidden;
}

.plan-progress__fill {
  height: 100%;
  border-radius: 2px;
  background: #61aed4;
  transition: width 0.3s ease;
}

.plan-intro {
  margin-bottom: 1.5rem;
}

/* ─── Section dividers ─── */

.section-divider {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 2.5rem 0 1.5rem;
}

.section-divider__label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  white-space: nowrap;
}

.section-divider__rule {
  flex: 1;
  height: 1px;
  background: var(--color-border, #e2e5e9);
}

.section-preamble {
  margin-bottom: 1.5rem;
}

/* ─── Priority articles ─── */

.priority {
  margin-bottom: 2.5rem;
  scroll-margin-top: 1rem;
}

.priority__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.priority__title {
  font-family: 'PT Serif', serif;
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.3;
}

.priority-pill {
  flex-shrink: 0;
  padding: 0.2rem 0.7rem;
  border-radius: 999px;
  border: 1px solid;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  white-space: nowrap;
}

.priority-pill--critical {
  color: #1a5f8a;
  background: #cfe3f1;
  border-color: #b5d4e8;
}

.priority-pill--secondary {
  color: #3d8bb5;
  background: #daeef8;
  border-color: #c3e2f2;
}

.priority-pill--minor {
  color: #6b93ab;
  background: #f0f5f9;
  border-color: #dde8ef;
}

/* ─── Action checklists ─── */

.action-list {
  list-style: none;
  margin: 1rem 0 0 0.5rem;
  padding: 0;
}

.action-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
}

.action-item__label {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  cursor: pointer;
  flex: 1;
  min-width: 0;
}

/* Ghost icon: always present, visually recessive until hovered */
.action-item__dismiss {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-top: 2px;
  padding: 0;
  border: none;
  background: none;
  box-shadow: none;
  border-radius: 4px;
  color: #c8d0d7;
  cursor: pointer;
  transition: all 0.15s ease;
}

.action-item__dismiss:hover {
  color: var(--color-text-primary);
  background: var(--color-bg-tertiary, #f3f4f6);
  box-shadow: none;
  transform: none;
}

/* Dismissed: kept visible but muted; distinct from completed (no strikethrough) */
.action-item--dismissed .action-item__text {
  color: #a8b3bf;
}

.action-item--dismissed .action-item__checkbox {
  border-color: #e1e7ec;
  cursor: default;
}

.action-item__checkbox {
  appearance: none;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 4px;
  border: 1.5px solid #cbd5e0;
  border-radius: 3px;
  cursor: pointer;
  position: relative;
  transition: all 0.15s ease;
}

.action-item__checkbox:checked {
  background: #1a1f23;
  border-color: #1a1f23;
}

.action-item__checkbox:checked::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 1.5px;
  width: 4px;
  height: 9px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.action-item__text {
  font-family: 'PT Serif', serif;
  font-size: 16px;
  line-height: 1.6;
}

/* The checkbox replaces the outer list marker for full-markdown actions.
   Compound selector: must outrank the later `.plan-richtext :deep(ol)` margin
   rule, whose top margin pushed action text below the checkbox. */
.plan-richtext.action-item__text :deep(> ol),
.plan-richtext.action-item__text :deep(> ul) {
  list-style: none;
  margin: 0;
  padding: 0;
}

/* Numbered steps keep their number, rendered inline; bulleted actions use the
   checkbox as their only marker */
.plan-richtext.action-item__text :deep(> ol > li) {
  list-style: decimal inside;
  margin-bottom: 0;
}

.plan-richtext.action-item__text :deep(> ul > li) {
  list-style: none;
  margin-bottom: 0;
}

.plan-richtext.action-item__text :deep(li ul),
.plan-richtext.action-item__text :deep(li ol) {
  margin: 0.35rem 0 0.25rem 1.4rem;
}

.plan-richtext.action-item__text :deep(li ul li) {
  list-style: disc;
  margin-bottom: 0.25rem;
}

.action-item__label--done .action-item__text {
  color: #94a3b8;
  text-decoration: line-through;
}

/* ─── Show more ─── */

.show-more {
  display: block;
  margin: 0 auto 2rem;
  padding: 0.4rem 1rem;
  border: none;
  background: none;
  box-shadow: none;
  color: #4a94ba;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
}

.show-more:hover {
  text-decoration: underline;
}

/* ─── Phase callouts (full-bleed bands) ─── */

.phase-callout {
  margin: 1.5rem -72px;
  padding: 1.5rem 72px;
  background: var(--color-bg-secondary, #f8f9fa);
  border-top: 1px solid var(--color-border, #eceff2);
  border-bottom: 1px solid var(--color-border, #eceff2);
}

@media (max-width: 1040px) {
  .phase-callout {
    margin: 1.5rem -48px;
    padding: 1.5rem 48px;
  }
}

@media (max-width: 768px) {
  .phase-callout {
    margin: 1.5rem -24px;
    padding: 1.25rem 24px;
  }
}

.phase-callout__name {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 0.5rem;
}

.phase-callout__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.phase-chip {
  padding: 0.25rem 0.7rem;
  border-radius: 6px;
  border: 1px solid var(--color-border, #d0d7de);
  background: white;
  box-shadow: none;
  color: var(--color-text-secondary);
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.phase-chip:hover {
  transform: none;
  box-shadow: none;
}

.phase-chip:hover {
  border-color: #61aed4;
  color: #1a5f8a;
}

/* ─── Markdown typography ─── */

.plan-richtext {
  font-family: 'PT Serif', serif;
  font-size: 16px;
  line-height: 1.75;
  color: var(--color-text-primary);
}

.plan-richtext :deep(p) {
  margin-bottom: 1rem;
}

.plan-richtext :deep(p:last-child) {
  margin-bottom: 0;
}

.plan-richtext :deep(strong) {
  font-weight: 600;
}

.plan-richtext :deep(em) {
  font-style: italic;
}

.plan-richtext :deep(ul),
.plan-richtext :deep(ol) {
  margin: 0.5rem 0 1rem 1.5rem;
}

.plan-richtext :deep(li) {
  margin-bottom: 0.5rem;
}

.plan-richtext :deep(ul li) {
  list-style: disc;
}

/* Model output often includes --- breaks; the view's own dividers replace them */
.plan-richtext :deep(hr) {
  display: none;
}

.plan-richtext :deep(ol li) {
  list-style: decimal;
}
</style>
